import { useCallback, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import type { FileQuerySchema } from '@/_schemas/file';
import type { OnUpdate } from '@/_schemas/types';
import { resetFormState } from '@/_shared';

import { fileFormToUpdate } from './shared';

export function useMedia<T>(onPartialUpdate: OnUpdate<T>, shouldResetEdit = true) {
  const form = useFormContext();
  const [isPending, setIsPending] = useState(false);

  const onImageUpdate = useCallback(
    async (t: 'CREATE' | 'UPDATE' | 'DELETE' | 'DELETED', prop: string, v: FileQuerySchema[]) => {
      if (t === 'DELETED') {
        return undefined;
      }

      let msg = 'Image has been added.';
      if (t === 'UPDATE') {
        msg = 'Image has been updated.';
      } else if (t === 'DELETE') {
        msg = 'Image has been deleted.';
      }

      setIsPending(true);

      try {
        const res = await onPartialUpdate(
          {
            [prop]: fileFormToUpdate(v),
          } as T,
          msg,
          shouldResetEdit,
          false
        );

        if (res && form) {
          const allFormValues = form.getValues();
          resetFormState(form, { ...allFormValues, [prop]: v });
        }

        setIsPending(false);
        return res;
      } catch (error) {
        console.error(error);
      } finally {
        setIsPending(false);
      }
      return undefined;
    },
    [form, shouldResetEdit, onPartialUpdate]
  );

  return { onImageUpdate, isPending };
}
